import img2 from "../assests/images/green-logo.png";

import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";

import {
  ChevronDown,
  Heart,
  LogOut,
  Menu,
  Search,
  User,
  X,
  MessageCircle,
} from "lucide-react";

export default function Header({
  onLoginClick,
  user,
  setUser,
}) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileUserOpen, setMobileUserOpen] = useState(false);

  const dropdownRef = useRef(null);
  const mobileUserRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  /*
  |--------------------------------------------------------------------------
  | SCROLL DETECTION
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  /*
  |--------------------------------------------------------------------------
  | CLOSE DROPDOWNS WHEN CLICKING OUTSIDE
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setDropdownOpen(false);
      }

      if (
        mobileUserRef.current &&
        !mobileUserRef.current.contains(event.target)
      ) {
        setMobileUserOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );
    };
  }, []);

  /*
  |--------------------------------------------------------------------------
  | CLOSE MOBILE MENU WHEN ROUTE CHANGES
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileUserOpen(false);
    setDropdownOpen(false);
  }, [location.pathname]);

  /*
  |--------------------------------------------------------------------------
  | PREVENT BODY SCROLL WHEN MOBILE MENU IS OPEN
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  /*
  |--------------------------------------------------------------------------
  | HELPERS
  |--------------------------------------------------------------------------
  */

  const navigateTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setMobileUserOpen(false);
  };

  const handleHomeClick = () => {
    closeMobileMenu();

    if (location.pathname === "/") {
      navigateTop();
    }
  };

  const handleBookNow = () => {
    closeMobileMenu();
    navigate("/hotels");
  };

  /*
  |--------------------------------------------------------------------------
  | LOGOUT
  |--------------------------------------------------------------------------
  */

  const logout = () => {
    localStorage.removeItem("user");

    if (setUser) {
      setUser(null);
    }

    setDropdownOpen(false);
    setMobileUserOpen(false);
    setMobileMenuOpen(false);

    navigate("/");

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  /*
  |--------------------------------------------------------------------------
  | DESKTOP NAVIGATION
  |--------------------------------------------------------------------------
  */

  const desktopNavClass = ({ isActive }) => `
    relative
    inline-flex
    items-center
    whitespace-nowrap
    px-1
    py-2
    text-[13px]
    font-medium
    tracking-[-0.01em]
    text-[#173D3D]
    transition-all
    duration-200

    after:absolute
    after:bottom-0
    after:left-1/2
    after:h-[2px]
    after:-translate-x-1/2
    after:rounded-full
    after:bg-[#0B6E8E]
    after:transition-all
    after:duration-200

    ${isActive
      ? "font-semibold text-[#0B6E8E] after:w-full"
      : "after:w-0 hover:text-[#0B6E8E] hover:after:w-full"
    }
  `;

  /*
  |--------------------------------------------------------------------------
  | MOBILE NAVIGATION
  |--------------------------------------------------------------------------
  */

  const mobileNavClass = ({ isActive }) => `
    flex
    w-full
    items-center
    justify-between
    rounded-xl
    px-4
    py-3.5
    text-sm
    font-medium
    transition-all
    duration-200

    ${isActive
      ? "bg-[#EFF8FB] font-semibold text-[#0B6E8E]"
      : "text-[#173D3D] hover:bg-[#F7FAFB] hover:text-[#0B6E8E]"
    }
  `;

  return (
    <>
      {/* =====================================================
          MAIN HEADER
      ===================================================== */}

      <header
        className={`
    fixed
    left-0
    z-[1000]
    w-full
    transition-all
    duration-300
py-1
    ${isScrolled
            ? "top-0"
            : "top-[36px]"
          }

    border-b
    bg-white

    ${isScrolled
            ? "border-[#E6EEF2] shadow-[0_5px_25px_rgba(6,47,79,0.10)]"
            : "border-[#EEF2F4]"
          }
  `}
      >
        <div
          className="
            mx-auto
            w-full
            max-w-[1536px]
            px-4
            sm:px-6
            lg:px-8
          "
        >
          <div
            className="
              flex
              min-h-[72px]
              items-center
              justify-between
              gap-5
              lg:min-h-[76px]
            "
          >
            {/* =================================================
                LOGO
            ================================================= */}

            <NavLink
              to="/"
              onClick={handleHomeClick}
              className="
                relative
                z-10
                flex
                shrink-0
                items-center
              "
              aria-label="Cheap Dubai Hotels"
            >
              <img
                src={img2}
                alt="Cheap Dubai Hotels"
                className="
                  block
                  h-auto
                  w-[145px]
                  object-contain
                  transition-all
                  duration-300
                  sm:w-[165px]
                  lg:w-[250px]
                "
              />
            </NavLink>

            {/* =================================================
                DESKTOP NAVIGATION
            ================================================= */}

            <nav
              className="
                hidden
                flex-1
                items-center
                justify-center
                gap-6
                lg:flex
                xl:gap-8
                2xl:gap-9
              "
              aria-label="Main navigation"
            >
              <NavLink
                to="/"
                onClick={handleHomeClick}
                className={desktopNavClass}
              >
                Home
              </NavLink>

              <NavLink
                to="/hotels"
                className={desktopNavClass}
              >
                Hotels
              </NavLink>

              {/* Existing /services route kept,
                  but displayed with the new homepage wording */}
              <NavLink
                to="/services"
                className={desktopNavClass}
              >
                Services
              </NavLink>

              <NavLink
                to="/about-us"
                className={desktopNavClass}
              >
                About Us
              </NavLink>

              <NavLink
                to="/blogs"
                className={desktopNavClass}
              >
                Blog
              </NavLink>

              <NavLink
                to="/contact-us"
                className={desktopNavClass}
              >
                Contact
              </NavLink>
            </nav>

            {/* =================================================
                DESKTOP RIGHT SIDE
            ================================================= */}

            <div
              className="
                hidden
                shrink-0
                items-center
                gap-3
                lg:flex
                xl:gap-4
              "
            >


              {/* WISHLIST */}

              <NavLink
                to="/wishlist"
                aria-label="Wishlist"
                className="
                  relative
                  flex
                  h-10
                  w-10
                  items-center
                  justify-center
                  rounded-full
                  text-[#0B3B60]
                  transition-all
                  duration-200
                  hover:bg-[#F1F7FA]
                  hover:text-[#0B6E8E]
                "
              >
                <Heart
                  size={19}
                  strokeWidth={1.9}
                />
              </NavLink>

              {/* USER */}

              {!user ? (
                <button
                  type="button"
                  onClick={onLoginClick}
                  aria-label="Sign in"
                  className="
                    flex
                    h-10
                    w-10
                    items-center
                    justify-center
                    rounded-full
                    text-[#0B3B60]
                    transition-all
                    duration-200
                    hover:bg-[#F1F7FA]
                    hover:text-[#0B6E8E]
                  "
                >
                  <User
                    size={19}
                    strokeWidth={1.9}
                  />
                </button>
              ) : (
                <div
                  ref={dropdownRef}
                  className="relative"
                >
                  <button
                    type="button"
                    onClick={() =>
                      setDropdownOpen((prev) => !prev)
                    }
                    className="
                      flex
                      items-center
                      gap-2
                      rounded-full
                      px-1.5
                      py-1.5
                      transition-all
                      duration-200
                      hover:bg-[#F4F8FA]
                    "
                  >
                    <span
                      className="
                        flex
                        h-8
                        w-8
                        items-center
                        justify-center
                        rounded-full
                        bg-[#0B3B60]
                        text-xs
                        font-bold
                        text-white
                      "
                    >
                      {user?.name
                        ?.charAt(0)
                        ?.toUpperCase() || "U"}
                    </span>

                    <ChevronDown
                      size={14}
                      className={`
                        text-[#62757D]
                        transition-transform
                        duration-200
                        ${dropdownOpen
                          ? "rotate-180"
                          : ""
                        }
                      `}
                    />
                  </button>

                  {/* USER DROPDOWN */}

                  {dropdownOpen && (
                    <div
                      className="
                        absolute
                        right-0
                        top-full
                        mt-3
                        w-52
                        overflow-hidden
                        rounded-2xl
                        border
                        border-[#E3EBEF]
                        bg-white
                        py-1
                        shadow-[0_18px_45px_rgba(6,47,79,0.14)]
                      "
                    >
                      <NavLink
                        to="/profile"
                        onClick={() =>
                          setDropdownOpen(false)
                        }
                        className="
                          flex
                          items-center
                          gap-3
                          px-4
                          py-3
                          text-sm
                          text-[#314F50]
                          transition
                          hover:bg-[#F4F8FA]
                          hover:text-[#0B6E8E]
                        "
                      >
                        <User size={17} />
                        My Profile
                      </NavLink>

                      <NavLink
                        to="/wishlist"
                        onClick={() =>
                          setDropdownOpen(false)
                        }
                        className="
                          flex
                          items-center
                          gap-3
                          px-4
                          py-3
                          text-sm
                          text-[#314F50]
                          transition
                          hover:bg-[#F4F8FA]
                          hover:text-[#0B6E8E]
                        "
                      >
                        <Heart size={17} />
                        Wishlist
                      </NavLink>

                      <div className="my-1 border-t border-[#EDF1F3]" />

                      <button
                        type="button"
                        onClick={logout}
                        className="
                          flex
                          w-full
                          items-center
                          gap-3
                          px-4
                          py-3
                          text-sm
                          text-red-500
                          transition
                          hover:bg-red-50
                        "
                      >
                        <LogOut size={17} />
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* BOOK NOW */}

              <button
                type="button"
                onClick={handleBookNow}
                className="
                  flex
                  h-11
                  items-center
                  justify-center
                  rounded-xl
                  bg-[#FF5A1F]
                  px-6
                  text-[12px]
                  font-bold
                  tracking-wide
                  text-white
                  shadow-[0_5px_18px_rgba(255,90,31,0.20)]
                  transition-all
                  duration-200
                  hover:-translate-y-0.5
                  hover:bg-[#F04F17]
                  hover:shadow-[0_8px_22px_rgba(255,90,31,0.28)]
                  active:translate-y-0
                  xl:px-7
                "
              >
                Book Now
              </button>
            </div>

            {/* =================================================
                MOBILE MENU BUTTON
            ================================================= */}

            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Open navigation menu"
              className="
                flex
                h-10
                w-10
                shrink-0
                items-center
                justify-center
                rounded-xl
                border
                border-[#DDE7EB]
                bg-white
                text-[#0B3B60]
                transition-all
                duration-200
                hover:border-[#BFD3DC]
                hover:bg-[#F5F9FA]
                lg:hidden
              "
            >
              <Menu size={22} />
            </button>
          </div>
        </div>
      </header>

      {/* =====================================================
          MOBILE OVERLAY
      ===================================================== */}

      <div
        onClick={closeMobileMenu}
        aria-hidden={!mobileMenuOpen}
        className={`
          fixed
          inset-0
          z-[1090]
          bg-[#062F4F]/55
          backdrop-blur-sm
          transition-opacity
          duration-300
          lg:hidden

          ${mobileMenuOpen
            ? "visible opacity-100"
            : "pointer-events-none invisible opacity-0"
          }
        `}
      />

      {/* =====================================================
          MOBILE SIDEBAR
      ===================================================== */}

      <aside
        aria-label="Mobile navigation"
        className={`
          fixed
          right-0
          top-0
          z-[1100]
          h-dvh
          w-[88%]
          max-w-[390px]
          overflow-y-auto
          bg-white
          shadow-2xl
          transition-transform
          duration-300
          ease-out
          lg:hidden

          ${mobileMenuOpen
            ? "translate-x-0"
            : "translate-x-full"
          }
        `}
      >
        {/* MOBILE HEADER */}

        <div
          className="
            flex
            items-center
            justify-between
            border-b
            border-[#E5EDF0]
            bg-white
            px-5
            py-5
          "
        >
          <NavLink
            to="/"
            onClick={handleHomeClick}
            className="flex items-center"
          >
            <img
              src={img2}
              alt="Cheap Dubai Hotels"
              className="
                h-auto
                w-[150px]
                object-contain
              "
            />
          </NavLink>

          <button
            type="button"
            onClick={closeMobileMenu}
            aria-label="Close navigation menu"
            className="
              flex
              h-10
              w-10
              items-center
              justify-center
              rounded-xl
              bg-[#F2F7F9]
              text-[#0B3B60]
              transition
              hover:bg-[#E8F1F4]
            "
          >
            <X size={22} />
          </button>
        </div>

        {/* MOBILE BOOK NOW */}

        <div className="px-5 pt-5">
          <button
            type="button"
            onClick={handleBookNow}
            className="
              flex
              w-full
              items-center
              justify-center
              rounded-xl
              bg-[#FF5A1F]
              px-4
              py-3.5
              text-sm
              font-bold
              text-white
              shadow-[0_5px_16px_rgba(255,90,31,0.20)]
              transition
              hover:bg-[#F04F17]
            "
          >
            Book Now
          </button>
        </div>

        {/* MOBILE USER */}

        <div
          className="
            border-b
            border-[#E8EEF0]
            px-5
            py-5
          "
        >
          {user ? (
            <div
              ref={mobileUserRef}
              className="relative"
            >
              <button
                type="button"
                onClick={() =>
                  setMobileUserOpen(
                    (prev) => !prev
                  )
                }
                className="
                  flex
                  w-full
                  items-center
                  gap-3
                  text-left
                "
              >
                <span
                  className="
                    flex
                    h-11
                    w-11
                    shrink-0
                    items-center
                    justify-center
                    rounded-full
                    bg-[#0B3B60]
                    text-base
                    font-bold
                    text-white
                  "
                >
                  {user?.name
                    ?.charAt(0)
                    ?.toUpperCase() || "U"}
                </span>

                <span className="min-w-0 flex-1">
                  <span
                    className="
                      block
                      truncate
                      text-sm
                      font-bold
                      text-[#173D3D]
                    "
                  >
                    {user?.name || "Account"}
                  </span>

                  <span
                    className="
                      block
                      text-xs
                      text-[#718080]
                    "
                  >
                    Tap to manage account
                  </span>
                </span>

                <ChevronDown
                  size={18}
                  className={`
                    text-[#718080]
                    transition-transform
                    duration-200

                    ${mobileUserOpen
                      ? "rotate-180"
                      : ""
                    }
                  `}
                />
              </button>

              {mobileUserOpen && (
                <div
                  className="
                    mt-3
                    overflow-hidden
                    rounded-xl
                    border
                    border-[#E3EBEF]
                    bg-[#F7FAFB]
                  "
                >
                  <NavLink
                    to="/profile"
                    onClick={closeMobileMenu}
                    className="
                      flex
                      items-center
                      gap-3
                      px-4
                      py-3
                      text-sm
                      text-[#314F50]
                      hover:bg-white
                      hover:text-[#0B6E8E]
                    "
                  >
                    <User size={17} />
                    My Profile
                  </NavLink>

                  <NavLink
                    to="/wishlist"
                    onClick={closeMobileMenu}
                    className="
                      flex
                      items-center
                      gap-3
                      px-4
                      py-3
                      text-sm
                      text-[#314F50]
                      hover:bg-white
                      hover:text-[#0B6E8E]
                    "
                  >
                    <Heart size={17} />
                    Wishlist
                  </NavLink>

                  <button
                    type="button"
                    onClick={logout}
                    className="
                      flex
                      w-full
                      items-center
                      gap-3
                      px-4
                      py-3
                      text-sm
                      text-red-500
                      hover:bg-red-50
                    "
                  >
                    <LogOut size={17} />
                    Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => {
                onLoginClick?.();
                closeMobileMenu();
              }}
              className="
                flex
                w-full
                items-center
                justify-center
                gap-2
                rounded-xl
                border
                border-[#DCE7EB]
                bg-[#F6FAFB]
                px-4
                py-3.5
                text-sm
                font-semibold
                text-[#0B3B60]
                transition
                hover:bg-[#EDF5F7]
              "
            >
              <User size={17} />
              Sign In / Register
            </button>
          )}
        </div>

        {/* MOBILE NAVIGATION */}

        <nav
          className="
            space-y-1
            px-4
            py-5
          "
        >
          <NavLink
            to="/"
            onClick={handleHomeClick}
            className={mobileNavClass}
          >
            Home
          </NavLink>

          <NavLink
            to="/hotels"
            onClick={closeMobileMenu}
            className={mobileNavClass}
          >
            Hotels
          </NavLink>

          <NavLink
            to="/services"
            onClick={closeMobileMenu}
            className={mobileNavClass}
          >
            Services
          </NavLink>

          <NavLink
            to="/about-us"
            onClick={closeMobileMenu}
            className={mobileNavClass}
          >
            About Us
          </NavLink>

          <NavLink
            to="/blogs"
            onClick={closeMobileMenu}
            className={mobileNavClass}
          >
            Blog
          </NavLink>

          <NavLink
            to="/contact-us"
            onClick={closeMobileMenu}
            className={mobileNavClass}
          >
            Contact
          </NavLink>

          {/* WISHLIST */}

          <NavLink
            to="/wishlist"
            onClick={closeMobileMenu}
            className="
              mt-4
              flex
              w-full
              items-center
              justify-center
              gap-2
              rounded-xl
              border
              border-[#DDE7EB]
              bg-white
              px-4
              py-3.5
              text-sm
              font-semibold
              text-[#0B3B60]
              transition
              hover:bg-[#F5F9FA]
            "
          >
            <Heart size={18} />
            Wishlist
          </NavLink>

          {/* WHATSAPP */}

          <a
            href="https://wa.me/971502477593"
            target="_blank"
            rel="noopener noreferrer"
            className="
              mt-3
              flex
              w-full
              items-center
              justify-center
              gap-2
              rounded-xl
              bg-[#062F4F]
              px-4
              py-3.5
              text-sm
              font-semibold
              text-white
              transition
              hover:bg-[#0B446A]
            "
          >
            <MessageCircle size={18} />
            Chat With Us
          </a>
        </nav>
      </aside>
    </>
  );
}